import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

/**
 * Route-level code splitting.
 *
 * Every page is lazy-loaded so visitors only download the JS they
 * actually need for the page they're on. Combined with AuthShell
 * (which is also lazy), this means:
 *
 *   - Public pages (/, /jannercard, /privacy, /terms, /f/:slug, 404)
 *     do NOT load the Clerk SDK. They get only React + Router +
 *     Helmet + their own page chunk.
 *
 *   - Auth pages (/sign-in, /sign-up, /dashboard, /onboarding) load
 *     AuthShell which pulls in @clerk/clerk-react on first navigation
 *     to any of them. After that, the chunk is cached.
 *
 * Pages where Clerk is the dominant cost (Dashboard, Onboarding) and
 * pages that genuinely need Clerk's UI components (SignIn, SignUp)
 * are the only ones that pay the Clerk cost. Marketing pages stay lean.
 */

// Public marketing & utility pages — no Clerk dependency
const LandingPage = lazy(() => import('./pages/LandingPage'))
const JannerCardPage = lazy(() => import('./pages/JannerCardPage'))
const HowToJoinJannerCardPage = lazy(() => import('./pages/HowToJoinJannerCardPage'))
const JannerCardFuturePage = lazy(() => import('./pages/JannerCardFuturePage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'))
const PublicFormPage = lazy(() => import('./pages/PublicFormPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

// Auth-dependent — these and AuthShell share the Clerk vendor chunk
const AuthShell = lazy(() => import('./AuthShell'))
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))

/**
 * Minimal placeholder shown while a route chunk downloads.
 * Just gives the page the right background colour for the brief moment
 * before content appears. Most chunks load in under 200ms on a normal
 * connection, so users rarely see this; it exists mainly to prevent
 * a flash of unstyled white when the body class hasn't applied yet.
 */
function RouteFallback() {
  return <div className="min-h-screen bg-cream" aria-hidden />
}

export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public — no Clerk */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/jannercard" element={<JannerCardPage />} />
        <Route path="/blog/how-to-join-jannercard" element={<HowToJoinJannerCardPage />} />
        <Route path="/blog/jannercard-future-of-local-business" element={<JannerCardFuturePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/f/:slug" element={<PublicFormPage />} />

        {/* Auth — Clerk chunk loads on first hit to any of these */}
        <Route
          path="/sign-in/*"
          element={
            <AuthShell>
              <SignInPage />
            </AuthShell>
          }
        />
        <Route
          path="/sign-up/*"
          element={
            <AuthShell>
              <SignUpPage />
            </AuthShell>
          }
        />
        <Route
          path="/onboarding"
          element={
            <AuthShell protect>
              <OnboardingPage />
            </AuthShell>
          }
        />
        <Route
          path="/dashboard"
          element={
            <AuthShell protect>
              <DashboardPage />
            </AuthShell>
          }
        />

        {/* Legacy /login → /sign-in */}
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
